<script setup lang="ts">
import { BACKEND_URL } from '@/services/apiClient';
import { computed, ref, watch } from 'vue';

const props = defineProps({
  avatar: {
    type: String,
    default: ''
  },
  title: {
    type: String,
    default: ''
  },
  size: {
    type: Number,
    default: 32
  }
});

// Falls back to the study initial when there is no avatar or the image fails to load.
const loadError = ref(false);
watch(() => props.avatar, () => {
  loadError.value = false;
});

const avatarUrl = computed(() => `${BACKEND_URL}${props.avatar}`);
const showImage = computed(() => !!props.avatar && !loadError.value);
const initial = computed(() => props.title?.trim().charAt(0).toUpperCase() ?? '');

const computedSize = computed(() => `${props.size}px`);
const computedFontSize = computed(() => `${Math.round(props.size * 0.47)}px`);
// Keeps the corner rounding proportional so a 20px and a 40px avatar look like the same shape.
const computedRadius = computed(() => `${Math.max(4, Math.round(props.size * 0.25))}px`);
</script>

<template>
  <div class="study-avatar">
    <img v-if="showImage"
      :src="avatarUrl"
      :key="avatar"
      :alt="title"
      @error="loadError = true"/>
    <div v-else class="study-initial">{{ initial }}</div>
  </div>
</template>

<style scoped lang="scss">
@use '../../assets/variables.module' as *;
.study-avatar {
  flex: 0 0 auto;
  width: v-bind(computedSize);
  height: v-bind(computedSize);

  img {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    border: 1px solid hsla(0, 0%, 0%, 0.1);
    border-radius: v-bind(computedRadius);
    object-fit: cover;
    background-color: white;
  }
}
.study-initial {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border: 1px solid hsla(248.5, 100%, 40%, 0.4);
  border-radius: v-bind(computedRadius);
  background-color: $primary-color;
  color: white;
  font-size: v-bind(computedFontSize);
  font-weight: 600;
  line-height: 1;
  user-select: none;
}
</style>
